import { REPORTING_INSIGHTS_INTERACTION_ID } from '@/constants/localStorageKeys';
import type { PaymentSchema } from '$api-clients/generated';

export type ReportingInsightsPromptId =
  | 'AGENT_EFFICIENCY'
  | 'LEAD_SOURCE_ROI'
  | 'MARKET_DOMINANCE'
  | 'COMMISSION_ANALYSIS'
  | 'SEASONALITY';

export type ReportingInsightsChatRequestBody =
  | {
      promptId: ReportingInsightsPromptId;
      interactionId?: string;
      message?: never;
    }
  | {
      message: string;
      interactionId?: string;
      promptId?: never;
    };

export type ChatMessage = {
  id: string;
  role: 'assistant' | 'user';
  content: string;
  isError?: boolean;
};

export type PendingRequestType = 'prompt' | 'follow_up' | null;

export type ReportingInsightsChatQuery = NonNullable<
  PaymentSchema.paths['/reporting/insights/chat']['post']['parameters']['query']
>;
export type ReportingInsightsChatHistoryQuery = NonNullable<
  PaymentSchema.paths['/reporting/insights/chat/history']['get']['parameters']['query']
>;

export type HistoryMessage = {
  role: 'assistant' | 'user';
  content: string;
  displayContent?: string;
};

export type ReportingInsightsStarterPrompt = {
  id: ReportingInsightsPromptId;
  label: string;
};

export type StoredInteractionContext =
  | {
      interactionId: string;
    }
  | string
  | null;

export const INTRO_MESSAGE_ID = 'intro-message';

export const INITIAL_MESSAGE: ChatMessage = {
  id: INTRO_MESSAGE_ID,
  role: 'assistant',
  content:
    'Ask a question directly about your report data, or click one of the insight prompts below to start with a guided analysis. Some responses may take a little while, especially for more complex questions.',
};

export const STARTER_PROMPTS: ReportingInsightsStarterPrompt[] = [
  { id: 'AGENT_EFFICIENCY', label: 'Agent Efficiency' },
  { id: 'LEAD_SOURCE_ROI', label: 'Lead Source ROI' },
  { id: 'MARKET_DOMINANCE', label: 'Market Dominance' },
  { id: 'COMMISSION_ANALYSIS', label: 'Commission Analysis' },
  { id: 'SEASONALITY', label: 'Seasonality' },
];

export const getMessageId = () => `${Date.now()}-${Math.random().toString(16)}`;

export const getInteractionStorageKey = (viewReportAs: string) =>
  `${REPORTING_INSIGHTS_INTERACTION_ID}-${viewReportAs}`;

export const resolveStoredInteractionId = (
  storedContext: StoredInteractionContext,
): string | null => {
  if (!storedContext) {
    return null;
  }

  if (typeof storedContext === 'string') {
    return storedContext.trim() || null;
  }

  return storedContext.interactionId.trim() || null;
};

export const isHistoryMessage = (value: unknown): value is HistoryMessage => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const entry = value as {
    role?: unknown;
    content?: unknown;
    displayContent?: unknown;
  };

  const content =
    typeof entry.displayContent === 'string' &&
    entry.displayContent.trim().length > 0
      ? entry.displayContent
      : entry.content;

  return (
    (entry.role === 'assistant' || entry.role === 'user') &&
    typeof content === 'string' &&
    content.trim().length > 0
  );
};

export const scrollTranscriptToBottom = (
  transcriptElement: HTMLDivElement | null,
) => {
  if (!transcriptElement) {
    return;
  }

  transcriptElement.scrollTop = transcriptElement.scrollHeight;
};
